import { Link } from 'react-router-dom';
import { Badge } from '@/components/Badge';
import type { ModuleResponseSummary } from '@/lib/assessments';

type Props = {
  modules: ModuleResponseSummary[];
  linked: boolean;
  limit?: number;
};

export type DeficiencyEntry = {
  count: number;
  description: string;
  moduleId: number;
  moduleName: string;
};

export function rollupDeficiencies(
  modules: ModuleResponseSummary[],
  limit = 10,
): DeficiencyEntry[] {
  const out: DeficiencyEntry[] = [];
  for (const m of modules) {
    for (const w of m.summary.itemResponses.warnings) {
      out.push({
        count: w.count,
        description: w.description,
        moduleId: m.id,
        moduleName: m.name,
      });
    }
  }
  return out.sort((a, b) => b.count - a.count).slice(0, limit);
}

export function Deficiencies({ modules, linked, limit = 10 }: Props) {
  const entries = rollupDeficiencies(modules, limit);
  return (
    <section
      className="rounded-lg border border-border bg-surface p-7"
      data-testid="deficiencies"
    >
      <h2 className="font-display text-h3 font-semibold">Top deficiencies</h2>
      {entries.length === 0 ? (
        <p className="mt-4 text-body-sm text-text-secondary">No open warnings.</p>
      ) : (
        <ul className="mt-5 flex flex-col gap-3">
          {entries.map((e, i) => {
            const body = (
              <span className="flex items-start gap-3 text-body-sm">
                <Badge tone="danger">{e.count}</Badge>
                <span className="flex-1 text-text-primary">{e.description}</span>
                <span className="text-text-secondary">{e.moduleName}</span>
              </span>
            );
            return (
              <li key={`${e.moduleId}-${i}`}>
                {linked ? (
                  <Link to={`/module/${e.moduleId}`} className="block hover:opacity-80">
                    {body}
                  </Link>
                ) : (
                  body
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
